/**
 * Message API calls (read-only Telegram messages)
 */

import apiClient from './client';
import {
  InChatSearchParams,
  Message,
  MessageSearchParams,
  MessageSearchResponse,
  MessageContextResponse,
  MessageWindowParams,
  MessageWindowResponse,
  Chat,
} from '@/types/api';

// ============================================================================
// Messages
// ============================================================================

/**
 * Get messages in a chat, optionally bounded by message_id.
 * The endpoint returns a bare list — there is no envelope to unwrap.
 */
export const getMessages = async (params: MessageSearchParams): Promise<Message[]> => {
  const response = await apiClient.get<Message[]>('/messages', { params });
  return response.data;
};

/**
 * Get single message by ID
 */
export const getMessage = async (id: number): Promise<Message> => {
  const response = await apiClient.get<Message>(`/messages/${id}`);
  return response.data;
};

/**
 * Get message context (messages before and after)
 */
export const getMessageContext = async (
  chatId: number,
  messageId: number,
  contextSize: number = 5
): Promise<MessageContextResponse> => {
  const response = await apiClient.get<MessageContextResponse | Message[]>(
    `/messages/${messageId}/context`,
    {
      params: {
        chat_id: chatId,
        before: contextSize,
        after: contextSize,
      },
    }
  );

  const data = response.data;
  if (Array.isArray(data)) {
    if (data.length === 0) {
      throw new Error('No context messages returned');
    }
    const targetIndex = data.findIndex((msg) => msg.message_id === messageId);
    const targetMessage = targetIndex >= 0 ? data[targetIndex] : data[0];
    return {
      target_message: targetMessage,
      messages_before: targetIndex >= 0 ? data.slice(0, targetIndex) : [],
      messages_after: targetIndex >= 0 ? data.slice(targetIndex + 1) : data.slice(1),
    };
  }

  return data;
};

/**
 * Get multiple messages by their IDs in parallel
 * Returns a map of message_id to Message
 */
export const getMessagesBatch = async (
  chatId: number,
  messageIds: number[]
): Promise<Record<number, Message>> => {
  if (messageIds.length === 0) {
    return {};
  }

  try {
    const promises = messageIds.map((id) =>
      getMessageContext(chatId, id, 0).catch(() => null)
    );
    const results = await Promise.all(promises);

    const map: Record<number, Message> = {};
    results.forEach((context, index) => {
      if (context) {
        map[messageIds[index]] = context.target_message;
      }
    });
    return map;
  } catch (error) {
    console.error('Error fetching batch messages:', error);
    throw error;
  }
};

/**
 * Get message window before or after anchor
 */
export const getMessageWindow = async (
  params: MessageWindowParams
): Promise<MessageWindowResponse> => {
  const response = await apiClient.get<MessageWindowResponse>('/messages/window', { params });
  return response.data;
};

/**
 * Search inside ONE chat. Text mode matches message_text only — never file
 * names — so a file-heavy channel legitimately returns nothing.
 */
export const searchMessages = async (
  params: InChatSearchParams
): Promise<MessageSearchResponse> => {
  const response = await apiClient.get<MessageSearchResponse>('/messages/search', { params });
  return response.data;
};

/**
 * Get messages in a specific range
 */
export const getMessageRange = async (
  chatId: number,
  startMessageId: number,
  endMessageId: number
): Promise<Message[]> => {
  return getMessages({
    chat_id: chatId,
    start_id: startMessageId,
    end_id: endMessageId,
  });
};

// ============================================================================
// Chats
// ============================================================================

/**
 * Get manuscript-only chats
 */
export const getManuscriptChats = async (): Promise<Chat[]> => {
  const response = await apiClient.get<Chat[]>('/messages/chats', {
    params: { manuscript_only: true },
  });
  return response.data;
};

/**
 * Update chat fields
 */
export const updateChat = async (
  id: number,
  data: { is_manuscript_channel: boolean }
): Promise<Chat> => {
  const response = await apiClient.patch<Chat>(`/messages/chats/${id}`, data);
  return response.data;
};
